import {
  Share2,
  RefreshCw,
  Plus,
  Trash2,
  ShieldCheck,
  Lock,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Spinner,
  type SelectOption,
} from '../../ui';
import { useSharing } from '../../features/sharing/useSharing';
import { SCOPE_OPTIONS, type ShareScope } from '../../features/sharing/types';
import type { TenantShare } from '../../api';

const SCOPE_SELECT_OPTIONS: SelectOption[] = SCOPE_OPTIONS.map((o) => ({
  value: o.value,
  label: o.label,
}));

/**
 * Tenant Sharing — lists the caller tenant's active outbound share grants and
 * lets the team grant/revoke another tenant READ access to its Vector data.
 *
 * The page reaches the backend ONLY through the useSharing hook (which wraps
 * api.tenantAccess). Every action handles its error inline.
 */
export function SharingPage() {
  const s = useSharing();

  return (
    <div>
      <PageHeader
        icon={<Share2 className="h-5 w-5" />}
        title="Tenant Sharing"
        description="Grant other teams read access to your team's Vector."
        actions={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<RefreshCw className="h-4 w-4" />}
            loading={s.loading}
            onClick={() => void s.refreshShares()}
          >
            Refresh
          </Button>
        }
      />

      {/* Explainer */}
      <Card className="mb-6">
        <CardBody className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-surface-3 text-accent">
            <Lock className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="space-y-2 text-sm text-zinc-400">
            <p>
              Your team's Vector is{' '}
              <span className="text-zinc-200">private</span> by default. Grant
              another team <span className="text-zinc-200">read access</span>{' '}
              here to let them read your tenant's data. You always own the
              grants you extend — you can revoke any of them at any time.
            </p>
          </div>
        </CardBody>
      </Card>

      {/* Grant form */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Grant access</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Grantee tenant ID"
              placeholder="e.g. team-acme"
              value={s.form.granteeTenantId}
              onChange={(e) => s.setGranteeTenantId(e.target.value)}
              hint="The tenant you want to grant read access to."
            />
            <Select
              label="Scope"
              options={SCOPE_SELECT_OPTIONS}
              value={s.form.scope}
              onChange={(e) => s.setScope(e.target.value as ShareScope)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              loading={s.granting}
              disabled={!s.canGrant}
              onClick={() => void s.grant()}
            >
              Grant access
            </Button>
            {s.grantError && (
              <p className="text-sm text-red-400" role="alert">
                {s.grantError}
              </p>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Current shares */}
      <Card>
        <CardHeader>
          <CardTitle>Current shares</CardTitle>
        </CardHeader>
        <CardBody>
          {s.revokeError && (
            <p className="mb-3 text-sm text-red-400" role="alert">
              {s.revokeError}
            </p>
          )}
          {s.listError ? (
            <p className="text-sm text-red-400" role="alert">
              {s.listError}
            </p>
          ) : s.loading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner label="Loading shares" />
            </div>
          ) : s.shares.length === 0 ? (
            <EmptyState
              icon={<Share2 className="h-6 w-6" />}
              title="No active shares"
              description="You haven't granted any other tenant access yet. Use the form above to grant read access."
            />
          ) : (
            <ul className="space-y-2">
              {s.shares.map((share) => (
                <ShareRow
                  key={share.granteeTenantId}
                  share={share}
                  revoking={s.revokingId === share.granteeTenantId}
                  onRevoke={() => void s.revoke(share.granteeTenantId)}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

interface ShareRowProps {
  share: TenantShare;
  revoking: boolean;
  onRevoke: () => void;
}

/** One active outbound grant with a Revoke action. */
function ShareRow({ share, revoking, onRevoke }: ShareRowProps) {
  return (
    <li className="flex flex-wrap items-center gap-3 rounded-xl border border-surface-border bg-surface-1 px-3 py-2.5 text-sm">
      <ShieldCheck className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
      <span className="font-mono text-zinc-200">
        {share.granteeTenantId || '—'}
      </span>
      <Badge tone="info">{share.scope ?? 'READ'}</Badge>
      {share.grantedAt && (
        <span className="text-xs text-zinc-500">{share.grantedAt}</span>
      )}
      <Button
        variant="ghost"
        size="sm"
        className="ml-auto text-red-300 hover:bg-red-500/10"
        leftIcon={<Trash2 className="h-4 w-4" />}
        loading={revoking}
        aria-label={`Revoke access for ${share.granteeTenantId}`}
        onClick={onRevoke}
      >
        Revoke
      </Button>
    </li>
  );
}

export default SharingPage;
